import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, AlertCircle } from 'lucide-react';
import { ThemeId, themeById, prepareToets, toetsScore, saveThemeState, loadProgress, fmt, PASS, GOLD, ROYAL } from '../data/learning';
import { Track } from '../components/ui/learnbits';
import { CertificateButton } from '../components/ui/CertificateButton';
import { CountUp } from '../components/ui/motion';
import { playDone } from '../utils/sound';

const LETTERS = ['A', 'B', 'C', 'D'];

export const Toets: React.FC = () => {
  const { themeId } = useParams<{ themeId: string }>();
  const navigate = useNavigate();
  const t = themeById((themeId as ThemeId) || 'ai');
  // Vragen + opties geschud per poging (vast voor de duur van deze poging).
  const [qs, setQs] = useState(() => prepareToets(t.id));

  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState<(number | null)[]>(qs.map(() => null));
  const [done, setDone] = useState(false);

  const restart = () => { const nq = prepareToets(t.id); setQs(nq); setIdx(0); setAnswers(nq.map(() => null)); setDone(false); };

  const setAnswer = (v: number) => setAnswers((a) => a.map((x, i) => (i === idx ? v : x)));
  const last = idx === qs.length - 1;
  const { score, correct } = toetsScore(answers, qs);
  const passed = score >= PASS;

  const finish = () => { saveThemeState(t.id, { toets: score }); setDone(true); window.scrollTo(0, 0); if (score >= PASS) playDone(); };

  if (done) {
    const sa = loadProgress()[t.id].sa;
    const growth = sa != null ? Math.round((score - sa) * 10) / 10 : null;
    return (
      <div className="shell py-12 sm:py-16 max-w-readable">
        <div className="card p-8 text-center mb-5 anim-pop">
          <span className="grid place-items-center rounded-tile mx-auto mb-5" style={{ width: 56, height: 56, background: passed ? '#E3F0E8' : '#F6E7EA' }}>
            {passed ? <Check style={{ color: '#2B7A4B' }} strokeWidth={2.5} className="w-7 h-7" /> : <AlertCircle style={{ color: '#8A2C3D' }} strokeWidth={1.75} className="w-7 h-7" />}
          </span>
          <p className="label mb-2" style={{ color: passed ? '#2B7A4B' : '#8A2C3D' }}>{passed ? 'Geslaagd' : 'Net niet'}</p>
          <p className="font-display font-bold text-ink mb-1" style={{ fontSize: 60, lineHeight: 1 }}><CountUp to={score} decimals={1} duration={1200} /></p>
          <p className="text-sub mb-2" style={{ fontSize: 18 }}>{correct} van de {qs.length} goed</p>
          <p className="text-sub text-sm">{passed ? 'Goed gedaan! Je hebt dit thema onder de knie.' : 'Bijna! Kijk de lastige onderdelen nog even terug en probeer het opnieuw.'}</p>
        </div>

        {/* Zelfscan vs toets */}
        <div className="card p-6 mb-5">
          <h2 className="font-display font-semibold text-ink mb-4" style={{ fontSize: 16.5 }}>Je inschatting vs. je toets</h2>
          <div className="flex flex-col gap-2.5">
            <div className="flex items-center gap-3"><span className="text-sm w-20 text-sub">Zelfscan</span><div className="flex-1"><Track pct={sa != null ? sa * 10 : 0} color={GOLD} height={7} /></div><span className="text-sm tabular-nums w-10 text-right text-ink">{sa != null ? fmt(sa) : '—'}</span></div>
            <div className="flex items-center gap-3"><span className="text-sm w-20 text-sub">Toets</span><div className="flex-1"><Track pct={score * 10} color={ROYAL} height={7} /></div><span className="text-sm tabular-nums w-10 text-right text-ink">{fmt(score)}</span></div>
          </div>
          {growth != null && (
            <p className="text-sm text-sub mt-4">{growth > 0 ? `Je toets ligt ${fmt(Math.abs(growth))} punt hoger dan je eigen inschatting — mooie groei.` : growth < 0 ? `Je schatte jezelf ${fmt(Math.abs(growth))} punt hoger in dan de toets. Een herhaling helpt vast.` : 'Je inschatting en je toets liggen gelijk.'}</p>
          )}
        </div>

        {passed && (
          <div className="card p-5 mb-5 flex flex-col items-center gap-3 text-center" style={{ background: '#FCF7EA', border: '1px dashed #C29A33' }}>
            <p className="text-sm text-sub">Gefeliciteerd — je hebt je certificaat verdiend. Download het met je eigen naam (inloggen niet nodig).</p>
            <CertificateButton themeName={t.name} scoreLabel={`Eindscore ${fmt(score)}`} accent={t.accent} />
          </div>
        )}

        <div className="flex items-center justify-center gap-3 flex-wrap">
          <button onClick={() => navigate(`/thema/${t.id}`)} className="btn-primary">Terug naar thema</button>
          <button onClick={restart} className="btn-outline">Opnieuw maken</button>
        </div>
      </div>
    );
  }

  return (
    <div className="shell py-12 sm:py-16 max-w-readable">
      <Link to={`/thema/${t.id}`} className="inline-flex items-center gap-1.5 text-sm text-sub hover:text-ink mb-5">
        <ArrowLeft className="w-4 h-4" strokeWidth={1.75} /> {t.name}
      </Link>

      <div className="flex items-center gap-3 mb-6">
        <div className="flex-1"><Track pct={((idx + 1) / qs.length) * 100} color={ROYAL} /></div>
        <span className="text-sm text-sub tabular-nums">Vraag {idx + 1} / {qs.length}</span>
      </div>

      <p className="label mb-3">Toets</p>
      <h1 className="font-display font-bold text-ink mb-7" style={{ fontSize: 'clamp(24px,3.6vw,34px)', lineHeight: 1.2 }}>{qs[idx].q}</h1>

      <div className="flex flex-col gap-3 mb-8">
        {qs[idx].opts.map((opt, v) => {
          const sel = answers[idx] === v;
          return (
            <button key={v} onClick={() => setAnswer(v)} className="flex items-center gap-3.5 text-left rounded-lg p-4 transition-colors border"
              style={{ borderColor: sel ? '#2440CF' : '#E7E2DB', background: sel ? '#EEF1FD' : '#fff' }}>
              <span className="grid place-items-center rounded flex-none font-semibold" style={{ width: 30, height: 30, fontSize: 15, background: sel ? '#2440CF' : '#EFEAE3', color: sel ? '#fff' : '#6B6258' }}>{LETTERS[v]}</span>
              <span className="text-ink" style={{ fontSize: 16.5 }}>{opt}</span>
            </button>
          );
        })}
      </div>

      <div className="flex items-center justify-between">
        <button onClick={() => setIdx((i) => Math.max(0, i - 1))} className="btn-ghost" style={{ visibility: idx === 0 ? 'hidden' : 'visible' }}>
          <ArrowLeft className="w-4 h-4" strokeWidth={1.75} /> Vorige
        </button>
        <button disabled={answers[idx] == null} onClick={() => (last ? finish() : setIdx((i) => i + 1))} className="btn-primary">
          {last ? 'Toets afronden' : 'Volgende'} <ArrowRight className="w-4 h-4" strokeWidth={1.75} />
        </button>
      </div>
    </div>
  );
};
